import React, { useState, useEffect, useRef } from 'react';
import { X, Search, Check, ChevronDown, Crown, Edit3, User as UserIcon  } from 'lucide-react';
import { fetchLocalUser } from '../services/api/userService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faSearch } from '@fortawesome/free-solid-svg-icons';
import { User } from "../types/assistant";

interface CoOwnerModalProps {
  isOpen: boolean;
  onClose: () => void;
  assistantId?: string;
  appOwner?: string;
  onSave: (selectedUsers: UserWithRole[], assistantId: string) => void;
  initialSelectedUsers?: User[]; 
}

interface UserWithRole extends User {
  role: 'co-owner' | 'contributor';
}
const getUserInitials = (name: string, netId: string): string => {
  const displayName = name || netId;
  const parts = displayName.split(' ');
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return displayName.substring(0, 2).toUpperCase();
};

// Helper function to generate avatar colors
const getAvatarColor = (id: string): string => {
  // const colors = [
  //   'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-pink-500', 
  //   'bg-indigo-500', 'bg-red-500', 'bg-yellow-500', 'bg-teal-500'
  // ];
  const colors = [
    'bg-black'
  ];
  const index = id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[index % colors.length];
};

// Role Icon Component
const RoleIcon = ({ role, size = 12 }: { role: 'co-owner' | 'contributor', size?: number }) => {
  return role === 'co-owner' ? (
    <Crown size={size} className="text-yellow-600" />
  ) : (
    <Edit3 size={size} className="text-blue-600" />
  );
};

const UserAvatar = ({ user, userRole, size = 'md', showRoleBadge = false }: { 
  user: User, 
  userRole?: 'co-owner' | 'contributor',
  size?: 'sm' | 'md' | 'lg',
  showRoleBadge?: boolean 
}) => {
  const sizeClasses = {
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-12 h-12 text-base'
  };

  const badgeClasses = {
    sm: 'w-4 h-4 -bottom-0.5 -right-0.5',
    md: 'w-5 h-5 -bottom-1 -right-1',
    lg: 'w-6 h-6 -bottom-1 -right-1'
  };

  return (
    <div className="relative">
      <div className={`${sizeClasses[size]} ${getAvatarColor(user.net_id)} rounded-full flex items-center justify-center font-semibold text-white shadow-md`}>
        {getUserInitials(user.name, user.net_id)}
      </div>
      {showRoleBadge && userRole && (
        <div className={`absolute ${badgeClasses[size]} bg-white rounded-full flex items-center justify-center shadow-sm border-2 border-white`}>
          <RoleIcon role={userRole} size={size === 'sm' ? 8 : size === 'md' ? 10 : 12} />
        </div>
      )}
    </div>
  );
};

const RoleSelector = ({ 
  currentRole, 
  onChange, 
  isOpen, 
  onToggle, 
  userId 
}: {
  currentRole: 'co-owner' | 'contributor';
  onChange: (role: 'co-owner' | 'contributor') => void;
  isOpen: boolean;
  onToggle: () => void;
  userId: string;
}) => {
  return (
    <div className="relative">
      <button
        onClick={onToggle}
        className="flex items-center gap-2 px-3 py-2 bg-white border-2 border-gray-200 rounded-lg hover:border-gray-300 focus:outline-none focus:border-blue-500 transition-all duration-200 shadow-sm"
      >
        <RoleIcon role={currentRole} size={14} />
        <span className="text-sm font-medium text-gray-700 capitalize">{currentRole}</span>
        <ChevronDown size={14} className={`text-gray-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute right-0 mt-2 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-20 overflow-hidden">
          <button
            onClick={() => onChange('contributor')}
            className={`w-full text-left px-4 py-3 text-sm hover:bg-gray-50 transition-colors flex items-center gap-3 ${
              currentRole === 'contributor' ? 'bg-blue-50 border-l-4 border-blue-500' : ''
            }`}
          >
            <Edit3 size={16} className="text-blue-600" />
            <div>
              <div className="font-medium text-gray-900">Contributor</div>
              <div className="text-xs text-gray-500">Can edit & view</div>
            </div>
          </button>
          <button
            onClick={() => onChange('co-owner')}
            className={`w-full text-left px-4 py-3 text-sm hover:bg-gray-50 transition-colors flex items-center gap-3 ${
              currentRole === 'co-owner' ? 'bg-yellow-50 border-l-4 border-yellow-500' : ''
            }`}
          >
            <Crown size={16} className="text-yellow-600" />
            <div>
              <div className="font-medium text-gray-900">Co-owner</div>
              <div className="text-xs text-gray-500">Full access</div>
            </div>
          </button>
        </div>
      )}
    </div>
  );
};
// Helper function to clean up name field
const cleanUserName = (name: string): string => {
  return name.replace(/\r?\n.*$/, '').trim(); // Remove everything after \r\n or \n
};

// Helper function to extract designation from name field
const extractDesignation = (name: string): string => {
  const parts = name.split(/\r?\n/);
  return parts.length > 1 ? parts[1].trim() : '';
};

// Separate CoOwnerModal Component
const CoOwnerModal: React.FC<CoOwnerModalProps> = ({ isOpen, onClose, assistantId,appOwner, onSave, initialSelectedUsers }) => {
  
  const [searchText, setSearchText] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<UserWithRole[]>([]);
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [roleDropdownOpen, setRoleDropdownOpen] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const currentNetId = localStorage.getItem("netId");
  
  // Check if current user has edit permissions
  const currentUser = selectedUsers.find(user => user.net_id === currentNetId);
  const isOwner = currentNetId === appOwner;
  const isViewOnly = currentUser?.role === 'contributor' && !isOwner;

  // Process API response to clean up data
  const processUsers = (apiUsers: any[]): User[] => {
    return apiUsers.map(user => ({
      net_id: user.userId,
      name: cleanUserName(user.name),
      role: extractDesignation(user.name),
      email: ''
    }));
  };

  // Fetch all users on modal open
  useEffect(() => {
    if (isOpen) {
      const fetchInitialUsers = async () => {
        setLoading(true);
        try {
          const response = await fetchLocalUser(''); // Empty string to get all users
          
          // Handle different response formats
          const apiUsers = response?.users || response || [];
          const processedUsers = processUsers(apiUsers);
          
          setUsers(processedUsers);
          setDropdownOpen(true); // Show dropdown by default
        } catch (error) {
          console.error('Error fetching users:', error);
          setUsers([]);
        } finally {
          setLoading(false);
        }
      };
      fetchInitialUsers();
    }
  }, [isOpen]);

  useEffect(() => {
    if (initialSelectedUsers && isOpen) {
      const enrichedUsers: UserWithRole[] = initialSelectedUsers.map(user => {
        const role = user.role === 'co-owner' || user.role === 'contributor' ? user.role : 'contributor';
        return {
          ...user,
          role,
        };
      });
      setSelectedUsers(enrichedUsers);
    }
  }, [initialSelectedUsers, isOpen]);


  // Fetch users when search text changes
  useEffect(() => {
    if (!isOpen) return; // Don't fetch if modal is closed

    const fetchUsers = async () => {
      setLoading(true);
      try {
        const netId = localStorage.getItem("netId");
        const response = await fetchLocalUser(searchText.trim());
        
        // Handle different response formats
        const apiUsers = response?.users || response || [];
        const filteredUsers = apiUsers.filter(
          (user: { userId: string; name: string }) =>
            user.userId !== netId && user.userId !== appOwner
        );

        const finalUsers = processUsers ? processUsers(filteredUsers) : filteredUsers;
        setUsers(finalUsers);

      } catch (error) {
        console.error('Error fetching users:', error);
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(fetchUsers, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchText, isOpen]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setRoleDropdownOpen(null);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const filteredUsers = users.filter(user => 
    user.name.toLowerCase().includes(searchText.toLowerCase()) ||
    user.net_id.toLowerCase().includes(searchText.toLowerCase()) ||
    (user.role?.toLowerCase() ?? '').includes(searchText.toLowerCase())
  );


  const handleUserSelect = (user: User) => {
    if (isViewOnly) return; // Prevent action if view-only
    
    setSelectedUsers(prev => {
      const isSelected = prev.some(selected => selected.net_id === user.net_id);
      if (isSelected) {
        return prev.filter(selected => selected.net_id !== user.net_id);
      } else {
        return [...prev, { ...user, role: 'contributor' }];
      }
    });
  };

  const handleRoleChange = (userId: string, role: 'co-owner' | 'contributor') => {
    if (isViewOnly) return; // Prevent action if view-only
    
    setSelectedUsers(prev => 
      prev.map(user => 
        user.net_id === userId ? { ...user, role: role } : user
      )
    );
    setRoleDropdownOpen(null);
  };

  const handleSave = () => {
    if (assistantId && !isViewOnly) {
      onSave(selectedUsers, assistantId);
      handleClose();
    }
  };

  const handleClose = () => {
    setSearchText('');
    setUsers([]);
    setSelectedUsers([]);
    setDropdownOpen(false);
    setRoleDropdownOpen(null);
    onClose();
  };

  const removeSelectedUser = (userId: string) => {
    if (isViewOnly) return; // Prevent action if view-only
    setSelectedUsers(prev => prev.filter(user => user.net_id !== userId));
  };

  if (!isOpen) return null;

   return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto backdrop-blur-sm">
      <div className="flex items-center justify-center min-h-screen py-10">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col border border-gray-100">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-100 flex-shrink-0">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Share Assistant</h2>
              <p className="text-sm text-gray-500 mt-1">Add Co-owner/Contributor to your assistant</p>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-full"
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto flex-1 min-h-0">
            {/* Selected Users - Vertical List Layout */}
            {selectedUsers.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <UserIcon size={16} />
                  Selected Co-owner/Contributor ({selectedUsers.length})
                </h3>
                <div className="space-y-3">
                  {selectedUsers.map(user => {
                    const isCurrentUser = user.net_id === localStorage.getItem("netId");
                    return (
                      <div
                        key={user.net_id}
                        className="flex items-center gap-3 p-3 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 transition-all duration-200"
                      >
                        {/* User Avatar */}
                        <UserAvatar user={user} size="md" />
                        
                        {/* User Info */}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 truncate">
                            {user.name || user.net_id}
                          </div>
                          <div className="text-sm text-gray-500 truncate">
                            ID: {user.net_id}
                          </div>
                        </div>
                        
                        {/* Role Display with Icon and Text */}
                        <div className="flex items-center gap-2">
                          <div className="relative">
                            <button
                              onClick={() => !isCurrentUser && !isViewOnly && setRoleDropdownOpen(roleDropdownOpen === user.net_id ? null : user.net_id)}
                              className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all duration-200 ${
                                isCurrentUser || isViewOnly
                                  ? 'bg-gray-100 border-gray-200 cursor-not-allowed' 
                                  : 'bg-white border-gray-300 hover:border-gray-400 hover:shadow-sm cursor-pointer'
                              }`}
                              disabled={isCurrentUser || isViewOnly}
                            >
                              <RoleIcon role={user.role} size={16} />
                              <span className="text-sm font-medium text-gray-700 capitalize">
                                {user.role === 'co-owner' ? 'Co-owner' : 'Contributor'}
                              </span>
                              {!isCurrentUser && !isViewOnly && (
                                <ChevronDown 
                                  size={14} 
                                  className={`text-gray-500 transition-transform duration-200 ${
                                    roleDropdownOpen === user.net_id ? 'rotate-180' : ''
                                  }`} 
                                />
                              )}
                            </button>
                            
                            {roleDropdownOpen === user.net_id && !isCurrentUser && !isViewOnly && (
                              <div ref={dropdownRef} className="absolute top-full right-0 mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-20 overflow-hidden">
                                <button
                                  onClick={() => handleRoleChange(user.net_id, 'contributor')}
                                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 ${
                                    user.role === 'contributor' ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                                  }`}
                                >
                                  <Edit3 size={14} className="text-blue-600" />
                                  <div>
                                    <div className="font-medium text-gray-900">Contributor</div>
                                    <div className="text-xs text-gray-500">Can edit & view</div>
                                  </div>
                                </button>
                                <button
                                  onClick={() => handleRoleChange(user.net_id, 'co-owner')}
                                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 ${
                                    user.role === 'co-owner' ? 'bg-yellow-50 border-l-4 border-yellow-500' : ''
                                  }`}
                                >
                                  <Crown size={14} className="text-yellow-600" />
                                  <div>
                                    <div className="font-medium text-gray-900">Co-owner</div>
                                    <div className="text-xs text-gray-500">Full access</div>
                                  </div>
                                </button>
                              </div>
                            )}
                          </div>
                          
                          {/* Larger Delete Button */}
                          {!isCurrentUser && (
                            <button
                              onClick={() => removeSelectedUser(user.net_id)}
                              disabled={isViewOnly}
                              className={`ml-2 p-2 rounded-full transition-all duration-200 ${
                                isViewOnly 
                                  ? 'text-gray-300 cursor-not-allowed' 
                                  : 'text-gray-400 hover:text-red-600 hover:bg-red-100'
                              }`}
                            >
                              <X size={18} />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Search Input */}
            <div className="relative mb-4">
              <div className="relative">
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                  <Search size={18} className="text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onFocus={() => !isViewOnly && setDropdownOpen(true)}
                  disabled={isViewOnly}
                  className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl text-sm transition-all duration-200 ${
                    isViewOnly 
                      ? 'bg-gray-100 border-gray-200 cursor-not-allowed text-gray-500' 
                      : 'border-gray-200 bg-gray-50 focus:bg-white'
                  }`}
                  placeholder="Search for users to add..."
                />
              </div>
            </div>

            {/* User Dropdown */}
            {dropdownOpen && !isViewOnly && (
              <div className="border-gray-200 rounded-xl max-h-64 overflow-y-auto bg-white shadow-lg">
                {loading ? (
                  <div className="p-6 text-center text-gray-500">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                    <p className="mt-2 text-sm">Loading users...</p>
                  </div>
                ) : filteredUsers.length > 0 ? (
                  filteredUsers.map(user => {
                    const isSelected = selectedUsers.some(selected => selected.net_id === user.net_id);
                    return (
                      <div
                        key={user.net_id}
                        onClick={() => handleUserSelect(user)}
                        className={`p-4 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 flex items-center gap-4 transition-all duration-200 ${
                          isSelected ? 'bg-blue-50 border-l-4 border-blue-500' : 'hover:border-l-4 hover:border-blue-300'
                        }`}
                      >
                        <UserAvatar user={user} size="md" />
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-gray-900 truncate">{user.name}</div>
                          <div className="text-sm text-gray-500 truncate">{user.role}</div>
                          <div className="text-xs text-gray-400 truncate">ID: {user.net_id}</div>
                        </div>
                        {isSelected && (
                          <div className="bg-black rounded-full p-1">
                            <Check className="text-white" size={14} />
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  searchText.trim().length >= 2 &&
                  <div className="p-6 text-center text-gray-500 text-sm">
                  
                    <UserIcon size={24} className="mx-auto mb-2 text-gray-300" />
                    No users found
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-gray-100 bg-gray-50 flex-shrink-0 rounded-b-2xl">
            {/* Selected Users Preview */}
            <div className="flex items-center">
              {selectedUsers.length > 0 && (
                <div className="flex items-center gap-3">
                  <div className="flex items-center -space-x-2">
                    {selectedUsers.slice(0, 4).map((user, index) => (
                      <div key={user.net_id} style={{ zIndex: 4 - index }}>
                        <UserAvatar user={user} userRole={user.role} size="sm" showRoleBadge={true} />
                      </div>
                    ))}
                    {selectedUsers.length > 4 && (
                      <div className="w-8 h-8 bg-gray-400 rounded-full flex items-center justify-center text-xs font-semibold text-white border-2 border-white shadow-sm">
                        +{selectedUsers.length - 4}
                      </div>
                    )}
                  </div>
                  <span className="text-sm text-gray-600 font-medium">
                    {selectedUsers.length} selected
                  </span>
                </div>
              )}
            </div>
            
            <button
              onClick={handleSave}
              disabled={selectedUsers.length === 0 || isViewOnly}
              className={`px-6 py-3 rounded-md text-sm font-semibold transition-all duration-200 ${
                isViewOnly
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-black text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg disabled:shadow-none'
              }`}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoOwnerModal;